import { useCallback, useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { FpShell } from "@/pages/panchatanthra/fp/FpShell";
import { EmptyState } from "@/components/shared";
import { fpBase, inr, num2, useFpContext } from "@/lib/fp";
import { toast } from "sonner";

const CONFLICT_TONE = {
  "No Conflict": "bg-success/18 text-success",
  "Moderate Conflict": "bg-warning/25 text-[hsl(30_88%_30%)]",
  "High Conflict": "bg-destructive/15 text-destructive",
  None: "bg-muted text-muted-foreground",
};

const EVIDENCE_TONE = {
  "Fully evidenced": "bg-success/18 text-success",
  "Data not available": "bg-warning/25 text-[hsl(30_88%_30%)]",
  "Not configured": "bg-muted text-muted-foreground",
};

const show = (value, unit) => {
  if (value === null || value === undefined) return "—";
  if (unit === "₹") return inr(value);
  if (unit === "%") return `${num2(value)}%`;
  if (unit === "x") return `${num2(value)}x`;
  return num2(value);
};

const Score = ({ value }) => (
  <span className="font-mono text-sm tabular-nums text-navy">
    {value === null || value === undefined ? "—" : `${value}/10`}
  </span>
);

export default function FpValidation() {
  const { tenantId, period, basis } = useFpContext();
  const [data, setData] = useState(null);
  const [map, setMap] = useState(null);
  const [comments, setComments] = useState({});
  const [history, setHistory] = useState([]);

  const load = useCallback(async () => {
    if (!tenantId || !period) return;
    try {
      const [v, m, h] = await Promise.all([
        api.get(`${fpBase(tenantId)}/validation`, { params: { period, basis } }),
        api.get(`${fpBase(tenantId)}/evidence-map`),
        api.get(`${fpBase(tenantId)}/validation/history`),
      ]);
      setData(v.data);
      setMap(m.data);
      setHistory(h.data);
      setComments(Object.fromEntries(v.data.rows.map((r) => [r.question_code, r.comment || ""])));
    } catch (e) { setData({ error: errMsg(e) }); }
  }, [tenantId, period, basis]);

  useEffect(() => { load(); }, [load]);

  const link = async (question_code, parameter_id) => {
    try {
      await api.put(`${fpBase(tenantId)}/evidence-map`, { items: [{ question_code, parameter_id }] });
      toast.success(parameter_id ? "Evidence mapped" : "Evidence link removed");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const decide = async (row, choice) => {
    try {
      await api.post(`${fpBase(tenantId)}/validation/decision`,
                     { question_code: row.question_code, choice,
                       comment: comments[row.question_code] || "" },
                     { params: { period, basis } });
      toast.success(`${row.question_code}: ${choice === "survey" ? "Survey" : "Evidence"} score selected`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const approve = async () => {
    try {
      const { data: out } = await api.post(`${fpBase(tenantId)}/validation/approve`, null,
                                           { params: { period, basis } });
      toast.success(`Approved — Financial Performance rolls up at ${out.score10}/10`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const preview = data?.preview_rollup?.score10;
  const surveyScore = data?.survey_rollup?.score10;

  return (
    <FpShell
      title="Score validation & roll-up"
      description="Survey score beside the evidence score for every Financial Performance question. Nothing is averaged and nothing is overridden: a reviewer chooses one of the two for each question, gives a reason where the conflict band demands it, and only the approved score rolls up to Panchatanthra."
      actions={data?.can_approve && (
        <button onClick={approve} data-testid="fp-validation-approve"
                className="rounded-full bg-navy px-5 py-2.5 text-[10px] uppercase tracking-[0.14em] text-white hover:bg-navy/90">
          Approve & roll up
        </button>
      )}
    >
      {!period ? (
        <EmptyState testid="fp-validation-empty" title="Nothing uploaded yet"
                    message="Upload statements and approve the classification first." />
      ) : data?.error ? (
        <EmptyState testid="fp-validation-error" title="Unavailable" message={data.error} />
      ) : !data ? (
        <p className="label-caps animate-pulse">Building the validation preview</p>
      ) : (
        <div className="space-y-6" data-testid="fp-validation">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            {[["Survey score", surveyScore, "fp-rollup-survey"],
              ["Preview after choices", preview, "fp-rollup-preview"],
              ["Last approved", data.approved?.score10 ?? null, "fp-rollup-approved"]].map(
              ([label, value, testid]) => (
                <div key={label} className="panel p-5" data-testid={testid}>
                  <p className="label-caps">{label}</p>
                  <p className="mt-1 font-display text-3xl text-navy">
                    {value === null || value === undefined ? "—" : value}
                    <span className="text-base text-muted-foreground">/10</span>
                  </p>
                </div>
              ))}
            <div className="panel p-5" data-testid="fp-rollup-state">
              <p className="label-caps">Roll-up state</p>
              <p className="mt-1 text-sm text-navy">
                {data.pending_decisions.length
                  ? `${data.pending_decisions.length} question(s) awaiting a reviewer choice`
                  : data.comments_missing.length
                    ? `${data.comments_missing.length} reason(s) still required`
                    : "Ready for approval"}
              </p>
              <p className="mt-2 text-[11px] text-muted-foreground">
                {data.version_label} · {data.period_label || data.fiscal_year}
              </p>
            </div>
          </div>

          <div className="panel overflow-hidden" data-testid="fp-evidence-map">
            <div className="border-b border-border px-6 py-4">
              <p className="label-caps">Evidence mapping</p>
              <h3 className="font-display text-lg text-navy">
                Which question is backed by which client parameter
              </h3>
              {!map?.parameters?.length && (
                <p className="mt-1 text-[11px] text-[hsl(28_82%_38%)]">
                  No parameters selected yet — choose them on the Parameters & goals tab first.
                </p>
              )}
            </div>
            <div className="divide-y divide-border">
              {(map?.questions || []).map((q) => (
                <div key={q.question_code}
                     className="flex flex-wrap items-center justify-between gap-3 px-6 py-3"
                     data-testid={`fp-evidence-row-${q.question_code}`}>
                  <div className="min-w-0 max-w-2xl">
                    <p className="text-sm text-navy">{q.question_code} · {q.text}</p>
                    <p className="text-[10px] text-muted-foreground">{q.sub_element_name}</p>
                  </div>
                  <select value={q.parameter_id || ""} disabled={!map?.can_configure}
                          data-testid={`fp-evidence-select-${q.question_code}`}
                          onChange={(e) => link(q.question_code, e.target.value)}
                          className="rounded-full border border-border bg-card px-4 py-2 text-xs text-navy outline-none focus:border-terracotta disabled:opacity-60">
                    <option value="">No evidence — survey only</option>
                    {(map?.parameters || []).map((p) => (
                      <option key={p.parameter_id} value={p.parameter_id}>
                        {p.name}{p.goal !== null && p.goal !== undefined ? ` (goal ${p.goal})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>

          <div className="panel overflow-hidden" data-testid="fp-validation-table">
            <div className="border-b border-border px-6 py-4">
              <p className="label-caps">Validation preview · reviewer decision</p>
              <h3 className="font-display text-lg text-navy">Perception versus evidence</h3>
            </div>
            <div className="divide-y divide-border">
              {data.rows.map((r) => (
                <div key={r.question_code} className="px-6 py-4"
                     data-testid={`fp-validation-row-${r.question_code}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0 max-w-xl">
                      <p className="text-sm font-medium text-navy">{r.question_code} · {r.text}</p>
                      <p className="mt-0.5 text-[10px] text-muted-foreground">{r.sub_element_name}</p>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span className={`rounded-full px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.1em] ${
                          EVIDENCE_TONE[r.evidence_status] || "bg-muted"}`}>
                          {r.evidence_status}
                        </span>
                        <span className={`rounded-full px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.1em] ${
                          CONFLICT_TONE[r.conflict_level] || "bg-muted"}`}>
                          {r.conflict_level}
                          {r.conflict_difference !== null ? ` · ${r.conflict_difference}` : ""}
                        </span>
                        {r.parameter_name && (
                          <span className="text-[11px] text-muted-foreground">
                            {r.parameter_name}: actual {show(r.evidence_actual, r.unit)} vs goal{" "}
                            {show(r.evidence_goal, r.unit)}
                            {r.achievement_pct !== null ? ` (${r.achievement_pct}% achieved)` : ""}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-5">
                      <div className="text-right">
                        <p className="label-caps">Survey</p>
                        <Score value={r.survey_score10} />
                      </div>
                      <div className="text-right">
                        <p className="label-caps">Evidence</p>
                        <Score value={r.evidence_score10} />
                      </div>
                      <div className="text-right">
                        <p className="label-caps">Chosen</p>
                        <Score value={r.final_score10} />
                      </div>
                      {data.can_decide && (
                        <div className="flex overflow-hidden rounded-full border border-border">
                          {[["survey", "Survey"], ["evidence", "Evidence"]].map(([key, label]) => (
                            <button key={key} onClick={() => decide(r, key)}
                                    disabled={key === "evidence" && r.evidence_score10 === null}
                                    data-testid={`fp-choose-${key}-${r.question_code}`}
                                    className={`px-4 py-2 text-[10px] font-bold uppercase tracking-[0.12em] transition-colors disabled:opacity-40 ${
                                      r.choice === key ? "bg-terracotta text-white"
                                                       : "bg-card text-navy hover:bg-muted"}`}>
                              {label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {(r.comment_required !== "no" || r.comment) && data.can_decide && (
                    <input value={comments[r.question_code] ?? ""}
                           placeholder={r.comment_required === "yes"
                             ? "A reason is required before this choice can be recorded"
                             : "Reason (recommended)"}
                           data-testid={`fp-comment-${r.question_code}`}
                           onChange={(e) => setComments({ ...comments,
                                                          [r.question_code]: e.target.value })}
                           className="mt-3 w-full rounded-xl border border-border bg-card px-4 py-2.5 text-sm text-navy outline-none focus:border-terracotta" />
                  )}
                  {r.decided_by && (
                    <p className="mt-2 text-[10px] text-muted-foreground">
                      {r.choice === "survey" ? "Survey" : "Evidence"} score selected by{" "}
                      {r.decided_by} · {(r.decided_at || "").slice(0, 16).replace("T", " ")}
                      {r.comment ? ` · ${r.comment}` : ""}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="panel overflow-hidden" data-testid="fp-approval-history">
            <div className="border-b border-border px-6 py-4">
              <p className="label-caps">Approved score history</p>
              <h3 className="font-display text-lg text-navy">Every roll-up is kept</h3>
            </div>
            {!history.length ? (
              <p className="px-6 py-5 text-sm text-muted-foreground">
                Nothing approved yet. Until a reviewer approves, the Panchatanthra score is untouched.
              </p>
            ) : (
              <div className="divide-y divide-border">
                {history.map((h) => (
                  <div key={h.id} className="flex flex-wrap items-center justify-between gap-3 px-6 py-3"
                       data-testid={`fp-approval-${h.version}`}>
                    <div>
                      <p className="text-sm font-medium text-navy">
                        v{h.version} · {h.period_label || h.fiscal_year} · {h.score10}/10
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        Survey said {h.survey_score10 ?? "—"}/10 · approved by {h.approved_by_email} ·{" "}
                        {(h.approved_at || "").slice(0, 16).replace("T", " ")}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(h.sub_elements || []).map((s) => (
                        <span key={s.code} className="rounded-full border border-border px-3 py-1 text-[10px] text-navy">
                          {s.name}: {s.score10 ?? "—"}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </FpShell>
  );
}
